/**
 * Calls — live call log for the AI receptionist.
 *
 * Clients see (and hear) every call the AI answered for THEIR business as it
 * happens, instead of waiting for the monthly report. The backend guarantees
 * tenant scoping: /api/console/calls only ever returns this client's calls,
 * and the recording endpoint 404s anything that isn't theirs.
 *
 * Sections:
 *   - Ledger list of calls, newest first: when, who called, how long,
 *     urgency + booked chips, one-line summary.
 *   - Rows with a recording expand to an inline audio player. The <audio>
 *     element can't send the Authorization header, so the WAV is fetched
 *     authenticated → blob → object URL, lazily on first expand, and the
 *     URL is revoked when the row collapses or the screen unmounts.
 *   - "Load more" pagination appending pages of PAGE_SIZE.
 *   - Empty state, skeleton, and error states mirror Reports/Meetings.
 *
 * Data: GET /api/console/calls?limit&offset — first page under query key
 * ['calls'] (warmed by Shell's prefetch); later pages appended locally.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch, session } from '../lib/api.js';
import Skeleton from '../components/Skeleton.jsx';
import {
  Phone, PhoneIncoming, Play, Square, AlertCircle, CalendarCheck2, ChevronDown,
} from 'lucide-react';

const PAGE_SIZE = 50;

// ── Helpers ───────────────────────────────────────────────────────────────────

// Clients are Texas businesses, so call times render in Central Time with a
// "CT" label — consistent with Overview/Meetings. Without the fixed tz a
// travelling owner saw call times that disagreed with the rest of the app.
function formatWhen(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    timeZone: 'America/Chicago',
  }) + ' CT';
}

function formatDuration(seconds) {
  if (seconds == null) return null;
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** "+15125550147" → "(512) 555-0147"; anything unexpected passes through. */
function formatPhone(raw) {
  const m = String(raw || '').match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : (raw || '');
}

const URGENCY_STYLES = {
  EMERGENCY: { color: 'var(--bad, #b03434)', label: 'Emergency' },
  URGENT:    { color: 'var(--acc-ink, var(--acc))', label: 'Urgent' },
  ROUTINE:   { color: 'var(--ink-soft)', label: 'Routine' },
};

function ChipRow({ call }) {
  const urgency = URGENCY_STYLES[call.urgency];
  return (
    <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
      {urgency && (
        <span className="mono" style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: urgency.color }}>
          {urgency.label}
        </span>
      )}
      {call.appointment_booked && (
        <span className="mono" style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--good, #20713a)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <CalendarCheck2 size={11} strokeWidth={2.25} aria-hidden="true" /> Booked
        </span>
      )}
    </span>
  );
}

// ── Audio player (lazy blob fetch) ────────────────────────────────────────────

function RecordingPlayer({ callId }) {
  const [state, setState] = useState('loading'); // loading | ready | error
  const [src, setSrc] = useState(null);
  const urlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/console/calls/${callId}/recording.wav`, {
          headers: session.token ? { Authorization: `Bearer ${session.token}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        urlRef.current = url;
        setSrc(url);
        setState('ready');
      } catch {
        if (!cancelled) setState('error');
      }
    })();
    return () => {
      cancelled = true;
      if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    };
  }, [callId]);

  if (state === 'loading') {
    return <Skeleton style={{ height: 40, width: '100%' }} />;
  }
  if (state === 'error') {
    return (
      <p className="mono" style={{ margin: 0, fontSize: 11, color: 'var(--bad, #b03434)' }}>
        Couldn't load this recording — try again in a moment.
      </p>
    );
  }
  return (
    <audio
      controls
      src={src}
      preload="metadata"
      style={{ width: '100%', height: 40, display: 'block' }}
    >
      Your browser can't play this recording.
    </audio>
  );
}

// ── Call row ──────────────────────────────────────────────────────────────────

function CallRow({ call }) {
  const [open, setOpen] = useState(false);
  const who = call.customer_name || formatPhone(call.caller_phone) || 'Unknown caller';
  const duration = formatDuration(call.duration_seconds);
  const expandable = !!call.has_recording;

  return (
    <div className="lrow" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 0, padding: 0 }}>
      {/* Header line — the whole row toggles when there's a recording */}
      <div
        role={expandable ? 'button' : undefined}
        tabIndex={expandable ? 0 : undefined}
        aria-expanded={expandable ? open : undefined}
        aria-label={expandable ? `${open ? 'Hide' : 'Play'} recording of the call from ${who}` : undefined}
        onClick={expandable ? () => setOpen((v) => !v) : undefined}
        onKeyDown={expandable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((v) => !v); } } : undefined}
        style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: '15px 16px',
          cursor: expandable ? 'pointer' : 'default', minWidth: 0,
        }}
      >
        {/* Icon badge */}
        <div
          aria-hidden="true"
          style={{
            width: 40, height: 40, border: '1px solid var(--line)',
            background: 'var(--acc-soft)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexShrink: 0,
          }}
        >
          <PhoneIncoming size={17} color="var(--acc)" strokeWidth={1.75} />
        </div>

        {/* Who + summary */}
        <div style={{ minWidth: 0, flex: 1 }}>
          <p style={{ margin: '0 0 3px', fontSize: 15, fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{who}</span>
            <ChipRow call={call} />
          </p>
          <p className="mono" style={{ margin: 0, fontSize: 11.5, color: 'var(--ink-soft)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {formatWhen(call.started_at)}
            {duration ? ` · ${duration}` : ''}
            {call.summary_text ? ` — ${call.summary_text}` : ''}
          </p>
        </div>

        {/* Recording affordance */}
        {expandable ? (
          <span
            aria-hidden="true"
            className="mono"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--acc-ink, var(--acc))', flexShrink: 0 }}
          >
            {open ? <Square size={11} strokeWidth={2.25} /> : <Play size={11} strokeWidth={2.25} />}
            {open ? 'Hide' : 'Listen'}
            <ChevronDown size={13} strokeWidth={2} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 200ms var(--ez)' }} />
          </span>
        ) : (
          <span className="mono" style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-soft)', flexShrink: 0 }}>
            No recording
          </span>
        )}
      </div>

      {/* Expanded player — mounted only while open so blob fetch is lazy and
          the object URL is revoked on collapse (RecordingPlayer cleanup). */}
      {open && (
        <div style={{ padding: '0 16px 14px 70px' }}>
          <RecordingPlayer callId={call.id} />
        </div>
      )}
    </div>
  );
}

// ── Skeleton / error ──────────────────────────────────────────────────────────

function CallsSkeleton() {
  return (
    <div style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <Skeleton style={{ height: 30, width: '30%', marginBottom: 8 }} />
        <Skeleton style={{ height: 16, width: '60%' }} />
      </div>
      <div className="panel" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <i className="brk brk-tl" /><i className="brk brk-br" />
        <Skeleton style={{ height: 11, width: '20%', marginBottom: 4 }} />
        {[1, 2, 3, 4].map((i) => <Skeleton key={i} style={{ height: 72 }} />)}
      </div>
    </div>
  );
}

function CallsError({ error, refetch }) {
  return (
    <div style={{ padding: '20px 16px', paddingTop: 64, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 16 }}>
      <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--panel-2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <AlertCircle size={24} color="var(--ink-soft)" strokeWidth={1.5} aria-hidden="true" />
      </div>
      <div>
        <p className="d-xl" style={{ margin: '0 0 6px', fontSize: 22, color: 'var(--ink)' }}>
          Couldn't load your calls
        </p>
        <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 14, lineHeight: 1.5 }}>
          {error?.message || 'Something went wrong. Tap below to try again.'}
        </p>
      </div>
      <button type="button" onClick={() => refetch()} className="btnx primary">
        Try again
      </button>
    </div>
  );
}

// ── Main screen ───────────────────────────────────────────────────────────────

export default function Calls() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['calls'],
    queryFn: () => apiFetch(`/api/console/calls?limit=${PAGE_SIZE}`),
  });

  // Pages past the first are fetched imperatively and appended here; the
  // first page stays in react-query so Shell's prefetch keeps working.
  const [extra, setExtra] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);

  if (isLoading) return <CallsSkeleton />;
  if (error) return <CallsError error={error} refetch={refetch} />;

  const firstPage = data?.calls ?? [];
  const calls = [...firstPage, ...extra];
  const total = data?.total ?? calls.length;
  const hasMore = calls.length < total;

  async function loadMore() {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const next = await apiFetch(`/api/console/calls?limit=${PAGE_SIZE}&offset=${calls.length}`);
      setExtra((prev) => [...prev, ...(next?.calls ?? [])]);
    } catch {
      /* transient — the button stays for a retry */
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <div style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div>
        <h1 className="d-xl" style={{ fontSize: 30, color: 'var(--ink)', margin: '0 0 4px' }}>
          Calls
        </h1>
        <p className="serif-i" style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 16, lineHeight: 1.55 }}>
          Every call your AI receptionist has answered — listen back any time.
        </p>
      </div>

      {/* ── Call list ─────────────────────────────────────────────────────── */}
      <section aria-label="Answered calls" className="panel" style={{ padding: 20 }}>
        <i className="brk brk-tl" /><i className="brk brk-br" />
        <h2 className="mlabel" style={{ margin: '0 0 16px' }}>
          Call log{total ? ` · ${total}` : ''}
        </h2>

        {calls.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px 0 16px', gap: 12, textAlign: 'center' }}>
            <div aria-hidden="true" style={{ width: 52, height: 52, border: '1px solid var(--line)', background: 'var(--acc-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Phone size={24} color="var(--acc)" strokeWidth={1.5} />
            </div>
            <p style={{ margin: 0, fontSize: 15, color: 'var(--ink)', fontWeight: 600, lineHeight: 1.35 }}>
              No calls yet.
            </p>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-soft)', lineHeight: 1.5 }}>
              Your AI receptionist will log every call it answers here, the moment it ends.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {calls.map((call) => <CallRow key={call.id} call={call} />)}
          </div>
        )}

        {hasMore && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
            <button type="button" className="btnx quiet" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : `Load more (${calls.length} of ${total})`}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
